import React, { useCallback } from "react";
import classNames from "classnames";
import { Select } from "antd";
import isString from "lodash/isString";
import { useTranslation } from "react-i18next";
import Preloader from "../Preloader";
import { useIsTablet } from "../../utils/hooks/useIsTablet";
import { antdSelectFilterOption, customAntdSelectFilterOption } from "./methods";
import { useSelectOptions } from "./useSelectOptions";
import { useSelectValue } from "./useSelectValue";

const ResourceSelectLazyLoading = React.forwardRef((props, ref) => {
  const {
    className,
    apiRoute,
    onSelect,
    value,
    searchIn,
    mode,
    searchColumns,
    requestOnMount,
    onGetOptions,
    onLanguageChange,
    onDropdownVisibleChange,
    customParams,
    allowClear = true,
    perPage = 500,
    page = 1,
    initialValue = {},
    showSearch = true,
    showArrow = true,
    staticOptions = [],
    overwritePreviousOptions = false,
    refetchOptions = false,
    textAccessor = (option = {}) => option.text || option.name,
    valueAccessor = (option = {}) => option.id || option.value,
    onChange = (val) => val,
    transformer = (options) => options,
    beforeOptionRender = (option) => option,
    shouldReset,
    ...rest
  } = props;

  const isTablet = useIsTablet();
  const { t } = useTranslation();

  const { options, loading, serverSearch, onVisibleChange, onScroll, debouncedOnSearch } = useSelectOptions({
    apiRoute,
    customParams,
    initialValue,
    staticOptions,
    overwritePreviousOptions,
    perPage,
    page,
    textAccessor,
    valueAccessor,
    transformer,
    onGetOptions,
    onLanguageChange,
    onDropdownVisibleChange,
    searchColumns,
    searchIn,
    refetchOptions,
    requestOnMount,
    value
  });

  const { localValue, setLocalValue } = useSelectValue({ value, shouldReset, mode });

  const findOptionByValue = useCallback(
    (val) => options.find((option) => valueAccessor(option) === val) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options]
  );

  const onChangeValue = useCallback(
    (val) => {
      onChange(val, val ? findOptionByValue(val) : null);
      setLocalValue(val);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findOptionByValue]
  );

  const handleOnSelect = useCallback(
    (val) => {
      if (onSelect) {
        onSelect(val, findOptionByValue(val));
      }
    },
    [onSelect, findOptionByValue]
  );

  return (
    <Select
      ref={ref}
      size={isTablet ? undefined : "large"}
      allowClear={allowClear}
      mode={mode}
      showSearch={showSearch}
      suffixIcon={showArrow ? undefined : null}
      popupMatchSelectWidth={false}
      value={loading.onMount ? undefined : localValue}
      disabled={loading.onMount}
      optionFilterProp="children"
      className={classNames("resource-select", className, { "is-searchable": showSearch })}
      getPopupContainer={(el) => el}
      filterOption={(() => {
        if (serverSearch) return () => true;
        if (searchIn?.length > 0) return customAntdSelectFilterOption(searchIn);
        return antdSelectFilterOption;
      })()}
      onOpenChange={onVisibleChange}
      onPopupScroll={onScroll}
      onSearch={showSearch ? debouncedOnSearch : null}
      notFoundContent={
        loading.initial ? (
          <Preloader style={{ width: "100%", textAlign: "center" }} width={20} />
        ) : (
          <p className="m-0">{t("container.general_words.no_data_message")}</p>
        )
      }
      {...rest}
      onChange={onChangeValue}
      onSelect={handleOnSelect}
    >
      {options
        .filter((option) => option.value !== null && option.value !== undefined)
        .map((option) => {
          const preparedOption = beforeOptionRender(option);
          const text = preparedOption.transKey ? t(preparedOption.transKey) : preparedOption.text;
          return (
            <Select.Option
              key={preparedOption.value}
              value={preparedOption.value}
              title={isString(text) ? text : preparedOption.title}
              disabled={preparedOption.disabled}
              data={preparedOption}
              className={preparedOption.className}
            >
              {text}
              {preparedOption.subText && <div>{preparedOption.subText}</div>}
            </Select.Option>
          );
        })}
      {(loading.lazy || (options.length > 0 && loading.initial)) && (
        <Select.Option key="loading" disabled>
          <Preloader />
        </Select.Option>
      )}
    </Select>
  );
});

export default ResourceSelectLazyLoading;
